import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, dollars, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { Upload, Music, Disc, Crown, CheckCircle2, Download, BarChart3, UserCircle, Landmark, Gift, Sparkles, Wand2, RotateCcw, Image as ImageIcon } from "lucide-react";
import { Navigate } from "react-router-dom";
import ArtistAnalytics from "@/components/ArtistAnalytics";
import ProfileSettings from "@/components/ProfileSettings";
import ChangePassword from "@/components/ChangePassword";
import PayoutsPanel from "@/components/PayoutsPanel";
import ReferralsPanel from "@/components/ReferralsPanel";
import MeloMondayPanel from "@/components/MeloMondayPanel";
import PreviewStartPicker from "@/components/PreviewStartPicker";
import ExplicitBadge from "@/components/ExplicitBadge";
import PrintfulPanel from "@/components/PrintfulPanel";
import AlbumManagerPanel from "@/components/AlbumManagerPanel";
import NewTrackForm from "@/components/NewTrackForm";
import SocialKitModal from "@/components/SocialKitModal";
import MasteringModal from "@/components/MasteringModal";
import TrackPosterModal from "@/components/TrackPosterModal";
import PurchaseLibrary from "@/components/PurchaseLibrary";

export default function Dashboard() {
  const { user, refreshMe, loading } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [busy, setBusy] = useState(false);
  const coverRef = useRef();
  const [socialTrackId, setSocialTrackId] = useState(null);
  const [masterTrackId, setMasterTrackId] = useState(null);
  const [posterTrackId, setPosterTrackId] = useState(null);
  const [params, setParams] = useSearchParams();
  const tabFromUrl = params.get("tab");
  const [tab, setTab] = useState(tabFromUrl || "upload");
  const [genreOptions, setGenreOptions] = useState([
    "afrobeats", "alternative", "ambient", "americana", "blues", "bluegrass",
    "children", "christian", "classical", "country", "drum & bass", "edm",
    "electronic", "folk", "funk", "gospel", "hip-hop", "house", "hyperpop",
    "indie", "instrumental", "jazz", "k-pop", "latin", "lo-fi", "metal", "pop",
    "punk", "r&b", "reggae", "reggaeton", "rock", "singer-songwriter", "soul",
    "soundtrack", "spoken word", "techno", "trap", "world", "worship",
  ]);

  // Track form
  const [tForm, setTForm] = useState({ title: "", audio_path: "", duration_sec: 0, album_id: "", genre: "electronic", is_explicit: false, cover_path: "" });
  // Album form
  const [aForm, setAForm] = useState({ title: "", cover_path: "", description: "" });

  useEffect(() => {
    if (!user) return;
    api.get(`/tracks?artist_id=${user.id}`).then((r) => setTracks(r.data.tracks));
    api.get(`/albums?artist_id=${user.id}`).then((r) => setAlbums(r.data.albums));
    api.get("/purchases/mine").then((r) => setPurchases(r.data.purchases));
    api.get("/genres").then((r) => { if (r.data.genres?.length) setGenreOptions(r.data.genres); }).catch(() => {});
  }, [user]);

  const refreshTracks = async () => {
    if (!user) return;
    const r = await api.get(`/tracks?artist_id=${user.id}`);
    setTracks(r.data.tracks);
  };

  const revertMaster = async (t) => {
    if (!window.confirm(`Revert "${t.title}" back to the unmastered original?`)) return;
    try {
      const { data } = await api.post(`/tracks/${t.id}/master/revert`);
      setTracks((prev) => prev.map((x) => (x.id === t.id
        ? { ...x, audio_path: data.audio_path, original_audio_path: undefined, last_mastered_at: undefined }
        : x)));
      toast.success("Reverted to original");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const refreshAlbums = async () => {
    if (!user) return;
    const r = await api.get(`/albums?artist_id=${user.id}`);
    setAlbums(r.data.albums);
  };

  if (loading) return <div className="p-16 text-center font-mono text-xs uppercase text-gray-500">Loading...</div>;
  if (!user) return <Navigate to="/login" />;

  const isSubscribed = user && (user.subscription_active || user.bypass_paywall);

  const subscribe = async () => {
    try {
      const { data } = await api.post("/payments/checkout", { kind: "artist_sub", origin_url: window.location.origin });
      if (data.admin_free) { toast.success("Admin free access activated"); await refreshMe(); return; }
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); }
  };

  const uploadCover = async () => {
    const file = coverRef.current?.files?.[0];
    if (!file) return toast.error("Pick a cover image");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAForm({ ...aForm, cover_path: data.path });
      toast.success("Cover uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const createAlbum = async (e) => {
    e.preventDefault();
    if (!aForm.cover_path) return toast.error("Upload cover first");
    try {
      const { data } = await api.post("/albums", aForm);
      setAlbums([data, ...albums]);
      setAForm({ title: "", cover_path: "", description: "" });
      if (coverRef.current) coverRef.current.value = "";
      toast.success("Album created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 pb-32">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-2">Studio</div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter">{user.display_name || user.username}</h1>
          <div className="font-mono text-xs text-gray-500 mt-2 uppercase">@{user.username} · {user.role}</div>
        </div>
        {isSubscribed ? (
          <div className="flex items-center gap-2 border border-[#FF4500] px-4 py-2 bg-[#FF4500]/10">
            {user.bypass_paywall ? <Crown className="h-4 w-4 text-[#FF4500]" /> : <CheckCircle2 className="h-4 w-4 text-[#FF4500]" />}
            <span className="font-mono text-xs uppercase tracking-widest text-[#FF4500]">
              {user.bypass_paywall ? "Founder access" : "Artist plan active"}
            </span>
          </div>
        ) : (
          <Button data-testid="subscribe-artist-btn" onClick={subscribe}
                  className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11 px-6">
            Subscribe · $2/mo
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10">
        <Stat label="Tracks" value={tracks.length} max={user.bypass_paywall ? null : 150} />
        <Stat label="Albums" value={albums.length} />
        <Stat label="Total plays" value={tracks.reduce((s, t) => s + (t.plays || 0), 0)} />
        <Stat label="Sales" value={tracks.reduce((s, t) => s + (t.sales || 0), 0)} />
      </div>

      <Tabs value={tab} onValueChange={(v) => {
        setTab(v);
        // Only keep ?tab= in URL for non-default tabs; strip on 'upload' to keep URLs clean.
        if (v === "upload") { params.delete("tab"); setParams(params, { replace: true }); }
        else { params.set("tab", v); setParams(params, { replace: true }); }
      }}>
        <TabsList className="rounded-none bg-transparent border-b border-[#2A2A2A] w-full justify-start h-auto p-0 flex-wrap">
          <TabsTrigger data-testid="tab-upload" value="upload" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF4500] data-[state=active]:bg-transparent data-[state=active]:text-[#FF4500] font-mono text-xs uppercase tracking-widest">Upload</TabsTrigger>
          <TabsTrigger data-testid="tab-my-music" value="tracks" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF4500] data-[state=active]:bg-transparent data-[state=active]:text-[#FF4500] font-mono text-xs uppercase tracking-widest">My Music</TabsTrigger>
          <TabsTrigger data-testid="tab-analytics" value="analytics" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF4500] data-[state=active]:bg-transparent data-[state=active]:text-[#FF4500] font-mono text-xs uppercase tracking-widest"><BarChart3 className="h-3 w-3 mr-1 inline" />Analytics</TabsTrigger>
          <TabsTrigger data-testid="tab-profile" value="profile" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF4500] data-[state=active]:bg-transparent data-[state=active]:text-[#FF4500] font-mono text-xs uppercase tracking-widest"><UserCircle className="h-3 w-3 mr-1 inline" />Customize</TabsTrigger>
          <TabsTrigger data-testid="tab-purchases" value="purchases" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF4500] data-[state=active]:bg-transparent data-[state=active]:text-[#FF4500] font-mono text-xs uppercase tracking-widest">Library</TabsTrigger>
          <TabsTrigger data-testid="tab-payouts" value="payouts" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF4500] data-[state=active]:bg-transparent data-[state=active]:text-[#FF4500] font-mono text-xs uppercase tracking-widest">Payouts</TabsTrigger>
          <TabsTrigger data-testid="tab-referrals" value="referrals" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF4500] data-[state=active]:bg-transparent data-[state=active]:text-[#FF4500] font-mono text-xs uppercase tracking-widest">Referrals</TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="mt-8">
          {!isSubscribed && (
            <div className="border border-[#FF4500]/40 bg-[#FF4500]/5 p-6 mb-8">
              <div className="font-display text-xl mb-1">Subscribe to unlock uploads</div>
              <div className="font-mono text-xs text-gray-400">$2/month · sell up to 150 songs (~15 albums)</div>
            </div>
          )}
          {user.bypass_paywall && (
            <div className="border border-[#FF4500]/40 bg-[#FF4500]/5 p-4 mb-6 font-mono text-xs uppercase tracking-widest text-[#FF4500]">
              Founder access · Unlimited songs, no cap
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-6">
            <form onSubmit={createAlbum} className="border border-[#2A2A2A] p-6 bg-[#121212]">
              <div className="flex items-center gap-2 mb-4"><Disc className="h-5 w-5 text-[#FF4500]" /><h3 className="font-display text-xl">New album</h3></div>
              <Label className="font-mono text-xs uppercase tracking-widest text-gray-400">Cover image</Label>
              <div className="flex gap-2 mt-2">
                <input data-testid="album-cover-file" ref={coverRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif,.heic,image/jpeg,image/png,image/webp,image/gif,image/heic,image/*" className="flex-1 font-mono text-xs bg-black border border-[#2A2A2A] p-2" />
                <Button data-testid="album-cover-upload" type="button" onClick={uploadCover} disabled={busy || !isSubscribed} className="rounded-none bg-white/10 hover:bg-white/20 text-white font-mono text-xs uppercase h-10"><Upload className="h-4 w-4" /></Button>
              </div>
              {aForm.cover_path && <div className="mt-2"><img src={fileUrl(aForm.cover_path)} className="h-24 w-24 object-cover border border-[#2A2A2A]" alt="cover" /></div>}
              <Label className="font-mono text-xs uppercase tracking-widest text-gray-400 mt-4 block">Title</Label>
              <Input data-testid="album-title" required value={aForm.title} onChange={(e) => setAForm({ ...aForm, title: e.target.value })} className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono h-11" />
              <Label className="font-mono text-xs uppercase tracking-widest text-gray-400 mt-4 block">Description</Label>
              <Textarea data-testid="album-desc" value={aForm.description} onChange={(e) => setAForm({ ...aForm, description: e.target.value })} className="mt-2 rounded-none bg-black border-[#2A2A2A] font-mono" />
              <Button data-testid="album-submit" type="submit" disabled={!isSubscribed} className="w-full mt-6 rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-11">Create album</Button>
            </form>

            <NewTrackForm
              tForm={tForm}
              setTForm={setTForm}
              albums={albums}
              genreOptions={genreOptions}
              isSubscribed={isSubscribed}
              busy={busy}
              setBusy={setBusy}
              onCreated={(t) => setTracks([t, ...tracks])}
            />
          </div>

          {/* Melo Monday picker — lives at the bottom of Upload so artists
              actually find it after publishing a track (previously buried in Payouts). */}
          <div className="mt-8 max-w-3xl mx-auto">
            <MeloMondayPanel tracks={tracks} />
          </div>
        </TabsContent>

        <TabsContent value="tracks" className="mt-8">
          <div className="border border-[#FF4500]/30 bg-[#FF4500]/5 px-4 py-3 mb-6">
            <div className="font-mono text-xs uppercase tracking-widest text-[#FF4500] font-bold mb-1">How albums work</div>
            <div className="font-mono text-[11px] text-gray-300 leading-relaxed">
              Upload each song as a single first (Upload tab). Then use the <span className="text-[#FF4500]">"Manage songs"</span> button on any album below to tick which of your singles belong on that record. Up to 15 songs per album.
            </div>
          </div>

          <AlbumManagerPanel
            tracks={tracks}
            albums={albums}
            bypass={!!user.bypass_paywall}
            onTracksChanged={refreshTracks}
            onAlbumsChanged={refreshAlbums}
          />

          <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">
            All your tracks
          </div>
          <div className="border border-[#2A2A2A] divide-y divide-[#2A2A2A]">
            {tracks.map((t) => (
              <div key={t.id} className="px-3 py-3 hover:bg-white/[0.03] sm:grid sm:grid-cols-[1fr_auto_auto_auto_auto_auto_auto_auto_auto] sm:gap-4 sm:items-center sm:px-4">
                {/* Row 1: title + genre/album — full width on mobile, first cell on desktop */}
                <div className="min-w-0">
                  <div className="font-display truncate flex items-center gap-2 text-base">
                    <span className="truncate">{t.title}</span>
                    <ExplicitBadge explicit={t.is_explicit} trackId={t.id} size="xs" />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-2 mt-0.5 flex-wrap">
                    <span>{t.genre}</span>
                    {t.album_id && (
                      <span data-testid={`track-album-tag-${t.id}`} className="text-[#FF4500]">
                        · in {albums.find((a) => a.id === t.album_id)?.title || "album"}
                      </span>
                    )}
                    {!t.album_id && <span>· single</span>}
                    <span className="text-gray-600 sm:hidden">· {t.plays || 0} plays · {t.sales || 0} sales</span>
                  </div>
                </div>

                {/* Row 2 on mobile: controls in a wrapping flex, hidden columns on desktop */}
                <div className="mt-3 flex flex-wrap items-center gap-3 sm:hidden">
                  <PreviewStartPicker
                    track={t}
                    onSaved={(newStart) => setTracks(tracks.map((x) => x.id === t.id ? { ...x, preview_start_sec: newStart } : x))}
                  />
                  <label data-testid={`free-toggle-mobile-${t.id}`} className="flex items-center gap-2 cursor-pointer">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Free</span>
                    <Switch checked={!!t.is_free} onCheckedChange={async (v) => {
                      try { await api.patch(`/tracks/${t.id}/free`, { is_free: v });
                        setTracks(tracks.map((x) => x.id === t.id ? { ...x, is_free: v } : x));
                        toast.success(v ? "Track is now free" : "Track is now paid");
                      } catch (e) { toast.error(errMsg(e)); }
                    }} />
                  </label>
                  <label data-testid={`explicit-toggle-mobile-${t.id}`} className="flex items-center gap-2 cursor-pointer">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Explicit</span>
                    <Switch checked={!!t.is_explicit} onCheckedChange={async (v) => {
                      try { await api.patch(`/tracks/${t.id}/explicit`, { is_explicit: v });
                        setTracks(tracks.map((x) => x.id === t.id ? { ...x, is_explicit: v } : x));
                        toast.success(v ? "Marked explicit" : "Explicit flag removed");
                      } catch (e) { toast.error(errMsg(e)); }
                    }} />
                  </label>
                  <span className={`font-mono text-xs ml-auto ${t.is_free ? "text-[#FF4500]" : "text-gray-400"} whitespace-nowrap`}>
                    {t.is_free ? "FREE" : dollars(t.price_cents || 99)}
                  </span>
                  <button
                    data-testid={`track-social-mobile-${t.id}`}
                    onClick={() => setSocialTrackId(t.id)}
                    className="h-7 w-7 grid place-items-center border border-[#FF4500]/40 hover:border-[#FF4500] hover:bg-[#FF4500]/10 text-[#FF4500] transition-colors"
                    title="AI Social Kit"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`track-master-mobile-${t.id}`}
                    onClick={() => setMasterTrackId(t.id)}
                    className="h-7 w-7 grid place-items-center border border-[#FF4500]/40 hover:border-[#FF4500] hover:bg-[#FF4500]/10 text-[#FF4500] transition-colors"
                    title="AI Master (free)"
                  >
                    <Wand2 className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`track-poster-mobile-${t.id}`}
                    onClick={() => setPosterTrackId(t.id)}
                    className="h-7 w-7 grid place-items-center border border-[#FF4500]/40 hover:border-[#FF4500] hover:bg-[#FF4500]/10 text-[#FF4500] transition-colors"
                    title="Share poster (1080×1920)"
                  >
                    <ImageIcon className="h-3.5 w-3.5" />
                  </button>
                  {t.original_audio_path && (
                    <button
                      data-testid={`track-revert-mobile-${t.id}`}
                      onClick={() => revertMaster(t)}
                      className="h-7 w-7 grid place-items-center border border-gray-500/40 hover:border-white hover:text-white text-gray-400 transition-colors"
                      title="Revert to unmastered original"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                {/* Desktop-only cells (hidden on mobile — controls above handle mobile layout) */}
                <div className="hidden sm:block font-mono text-xs text-gray-400 whitespace-nowrap">{t.plays || 0} plays · {t.sales || 0} sales</div>
                <div className="hidden sm:block">
                  <PreviewStartPicker
                    track={t}
                    onSaved={(newStart) => setTracks(tracks.map((x) => x.id === t.id ? { ...x, preview_start_sec: newStart } : x))}
                  />
                </div>
                <label data-testid={`free-toggle-${t.id}`} className="hidden sm:flex items-center gap-2 cursor-pointer">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Free</span>
                  <Switch checked={!!t.is_free} onCheckedChange={async (v) => {
                    try { await api.patch(`/tracks/${t.id}/free`, { is_free: v });
                      setTracks(tracks.map((x) => x.id === t.id ? { ...x, is_free: v } : x));
                      toast.success(v ? "Track is now free" : "Track is now paid");
                    } catch (e) { toast.error(errMsg(e)); }
                  }} />
                </label>
                <label data-testid={`explicit-toggle-${t.id}`} className="hidden sm:flex items-center gap-2 cursor-pointer">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Explicit</span>
                  <Switch checked={!!t.is_explicit} onCheckedChange={async (v) => {
                    try { await api.patch(`/tracks/${t.id}/explicit`, { is_explicit: v });
                      setTracks(tracks.map((x) => x.id === t.id ? { ...x, is_explicit: v } : x));
                      toast.success(v ? "Marked explicit" : "Explicit flag removed");
                    } catch (e) { toast.error(errMsg(e)); }
                  }} />
                </label>
                <div className={`hidden sm:block font-mono text-xs ${t.is_free ? "text-[#FF4500]" : "text-gray-400"} whitespace-nowrap`}>
                  {t.is_free ? "FREE" : dollars(t.price_cents || 99)}
                </div>
                <button
                  data-testid={`track-social-${t.id}`}
                  onClick={() => setSocialTrackId(t.id)}
                  className="hidden sm:grid h-7 w-7 place-items-center border border-[#FF4500]/40 hover:border-[#FF4500] hover:bg-[#FF4500]/10 text-[#FF4500] transition-colors"
                  title="AI Social Kit"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`track-master-${t.id}`}
                  onClick={() => setMasterTrackId(t.id)}
                  className="hidden sm:grid h-7 w-7 place-items-center border border-[#FF4500]/40 hover:border-[#FF4500] hover:bg-[#FF4500]/10 text-[#FF4500] transition-colors"
                  title="AI Master (free)"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`track-poster-${t.id}`}
                  onClick={() => setPosterTrackId(t.id)}
                  className="hidden sm:grid h-7 w-7 place-items-center border border-[#FF4500]/40 hover:border-[#FF4500] hover:bg-[#FF4500]/10 text-[#FF4500] transition-colors"
                  title="Share poster (1080×1920)"
                >
                  <ImageIcon className="h-3.5 w-3.5" />
                </button>
                {t.original_audio_path && (
                  <button
                    data-testid={`track-revert-${t.id}`}
                    onClick={() => revertMaster(t)}
                    className="hidden sm:grid h-7 w-7 place-items-center border border-gray-500/40 hover:border-white hover:text-white text-gray-400 transition-colors"
                    title="Revert to unmastered original"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                )}
                {user.role === "admin" && (
                  <button
                    data-testid={`track-feature-${t.id}`}
                    onClick={async () => {
                      try {
                        const url = t.is_featured
                          ? `/admin/tracks/${t.id}/unfeature`
                          : `/admin/tracks/${t.id}/feature`;
                        await api.put(url);
                        setTracks(tracks.map((x) => x.id === t.id ? { ...x, is_featured: !t.is_featured } : x));
                        toast.success(t.is_featured ? "Un-featured" : "Featured on homepage");
                      } catch (e) { toast.error(errMsg(e)); }
                    }}
                    className={`hidden sm:grid h-7 w-7 place-items-center border transition-colors ${
                      t.is_featured
                        ? "border-[#FF4500] bg-[#FF4500] text-black"
                        : "border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500]"
                    }`}
                    title={t.is_featured ? "Un-feature" : "Feature on homepage"}
                  >
                    ★
                  </button>
                )}
              </div>
            ))}
            {tracks.length === 0 && <div className="p-8 text-center font-mono text-xs text-gray-500 uppercase">No tracks yet</div>}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-8">
          <ArtistAnalytics />
        </TabsContent>

        <TabsContent value="payouts" className="mt-8">
          <PayoutsPanel />
          <div className="mt-6">
            <PrintfulPanel />
          </div>
        </TabsContent>

        <TabsContent value="referrals" className="mt-8">
          <ReferralsPanel />
        </TabsContent>

        <TabsContent value="profile" className="mt-8">
          <ProfileSettings />
          <ChangePassword />
        </TabsContent>

        <TabsContent value="purchases" className="mt-8">
          <PurchaseLibrary purchases={purchases} />
        </TabsContent>
      </Tabs>

      {socialTrackId && (() => {
        const t = tracks.find((x) => x.id === socialTrackId);
        return t ? (
          <SocialKitModal
            kind="track"
            id={socialTrackId}
            title={t.title}
            onClose={() => setSocialTrackId(null)}
          />
        ) : null;
      })()}

      {masterTrackId && (() => {
        const t = tracks.find((x) => x.id === masterTrackId);
        return t ? (
          <MasteringModal
            track={t}
            onClose={() => setMasterTrackId(null)}
            onApplied={(newPath) =>
              setTracks(tracks.map((x) => (x.id === masterTrackId ? { ...x, audio_path: newPath } : x)))
            }
          />
        ) : null;
      })()}

      {posterTrackId && (() => {
        const t = tracks.find((x) => x.id === posterTrackId);
        return t ? (
          <TrackPosterModal
            track={t}
            onClose={() => setPosterTrackId(null)}
          />
        ) : null;
      })()}
    </div>
  );
}

function Stat({ label, value, max }) {
  return (
    <div className="border border-[#2A2A2A] p-4 md:p-6 bg-[#121212]">
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className="font-display text-3xl md:text-4xl mt-2">
        {value}{max ? <span className="text-gray-600 text-lg"> / {max}</span> : null}
      </div>
    </div>
  );
}
